import { useRouter } from 'next/navigation';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { UseComboboxSelectedItemChange } from 'downshift';
import { objectToCamel } from 'ts-case-convert';

import { GeographyTypeEnum, useDebouncedCallback } from '@nocowanie/common-ui';

import { AutocompleteItemModel, AutocompleteItemSCModel } from '@app/models/elastic-search';
import { ApiAutocompleteService } from '@app/services';

export interface UseAutocompleteDataProps {
    onAccommodationSelected?: () => void;
    onLocationSelected?: (item: AutocompleteItemModel) => void;
    initialQuery?: string;
}

export const useMapAutocompleteData = ({
    initialQuery,
    onAccommodationSelected,
    onLocationSelected,
}: UseAutocompleteDataProps = {}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [innerQuery, setInnerQuery] = useState(initialQuery || '');
    const [items, setItems] = useState<AutocompleteItemModel[] | null>(null);
    const router = useRouter();

    const onQueryChange = useDebouncedCallback(async (query: string) => {
        if (!query.trim().length) {
            setItems(null);
            return;
        }

        setIsLoading(true);

        try {
            const result = await ApiAutocompleteService.instance.getAutocompleteItems(
                query,
                5, // items count
            );

            const items =
                result?.hits?.map(({ _source }: { _source: AutocompleteItemSCModel }) =>
                    objectToCamel(_source),
                ) || [];

            setItems(items);
        } catch (error) {
            throw new Error(`[useAutocomplete] Request failed: ${error}`);
        } finally {
            setIsLoading(false);
        }
    }, 300);

    const userTyping = useRef(false);

    useEffect(() => {
        if (
            !userTyping.current &&
            initialQuery !== null &&
            initialQuery !== undefined &&
            initialQuery !== innerQuery
        ) {
            setInnerQuery(initialQuery);
        }
    }, [initialQuery, innerQuery]);

    const handleItemSelection = useCallback(
        (item: AutocompleteItemModel, triggerCallbacks = true) => {
            const { path, type } = item;
            const isAccommodation = type === GeographyTypeEnum.accommodation;

            if (isAccommodation && !item?.details?.ota) {
                if (triggerCallbacks) {
                    onAccommodationSelected?.();
                }

                requestAnimationFrame(() => {
                    router.push(path);
                });
                return true;
            } else {
                if (triggerCallbacks) {
                    onLocationSelected?.(item);
                }
                return false;
            }
        },
        [onAccommodationSelected, onLocationSelected, router],
    );

    const onSelectedItemChange = useCallback(
        (item: UseComboboxSelectedItemChange<AutocompleteItemModel>) => {
            handleItemSelection(item.selectedItem, true);
        },
        [handleItemSelection],
    );

    const setQuery = useCallback((value: React.SetStateAction<string>) => {
        userTyping.current = true;
        setInnerQuery(value);

        setTimeout(() => {
            userTyping.current = false;
        }, 0);
    }, []);

    return {
        initialQuery,
        query: innerQuery,
        setQuery,
        items,
        isLoading,
        onQueryChange,
        onSelectedItemChange,
        handleItemSelection,
        isMapVariant: true,
    };
};
